import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
const _excluded = ["dataQaType", "dataQaData", "label", "children", "leftContent", "rightContent", "error", "expandable", "filled", "focused", "readOnly", "disabled", "required", "cleanable", "cleanButtonAriaLabel", "flatCorners", "cursor", "informationContent", "hintBlock", "onClean", "inputIcon", "labelId", "labelFor", "labelAria", "labelOutside", "size", "valid", "counter", "scrollBar", "autofilled", "transparent", "cobrowsingSecure", "__overrideStyles", "variant", "dataInputType", "styleSlots", "UNSAFE_className", "UNSAFE_style", "className", "style", "styleSlots", "preserveSpaceForMobileCleanButton", "mobileCleanIgnoreFocus", "showAsterisk"];
import React, { forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react';
import cn from 'classnames';
import dataQa from '@platform-ui/dataQa';
import * as corners from '@platform-ui/flatCorners';
import Icon from '@platform-ui/icon';
import overrideStyles from '@platform-ui/overrideStyles';
import { useStyleSlots } from '@tui-react/core';
import TuiIconTdsSmallLock from '@tui-react/proprietary-icons/dist/svg/tuiIconTdsSmallLock.svg';
import TuiIconTdsSmallPragmaticCheck from '@tui-react/proprietary-icons/dist/svg/tuiIconTdsSmallPragmaticCheck.svg';
import TuiIconTdsSmallPragmaticCross from '@tui-react/proprietary-icons/dist/svg/tuiIconTdsSmallPragmaticCross.svg';
import CloseIosIcon from './clearfield-ios.svg';
import baseStyles from './InputBox.module.css';
const useIsMounted = () => {
  const isMounted = useRef(false);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  return isMounted;
};
const stopEventPropagate = event => {
  event.stopPropagation();
};
const handleTooltipClick = event => {
  stopEventPropagate(event);

  // в некоторых случаях компонент обернут в label и тогда нужно предотвратить поведение по умолчанию
  if (event.currentTarget.contains(event.target)) {
    event.preventDefault();
  }
};

// eslint-disable-next-line complexity
const InputBox = /*#__PURE__*/forwardRef((props, forwardedRef) => {
  const {
      dataQaType = 'uikit/inputBox',
      dataQaData,
      label,
      children,
      leftContent,
      rightContent,
      error,
      expandable,
      filled,
      focused,
      readOnly,
      disabled,
      required,
      cleanable,
      cleanButtonAriaLabel = 'Очистить',
      flatCorners,
      cursor,
      informationContent,
      hintBlock,
      onClean,
      inputIcon,
      labelId,
      labelFor,
      labelAria,
      labelOutside,
      size = 'l',
      valid,
      counter,
      scrollBar,
      autofilled,
      transparent,
      cobrowsingSecure,
      __overrideStyles,
      variant = 'default',
      dataInputType,
      UNSAFE_className,
      UNSAFE_style = {},
      className,
      style,
      styleSlots,
      preserveSpaceForMobileCleanButton = false,
      mobileCleanIgnoreFocus = false,
      showAsterisk = true
    } = props,
    otherProps = _objectWithoutPropertiesLoose(props, _excluded);
  const mobile = variant === 'mobile';
  const isIosInputSearch = mobile && dataInputType === 'search';
  const showDisabledIcon = mobile && disabled && !isIosInputSearch;
  const showClean = !readOnly && cleanable && filled && !mobile;
  const showMobileClean = !readOnly && cleanable && mobile && !disabled && ((mobileCleanIgnoreFocus || focused) && filled || preserveSpaceForMobileCleanButton);
  const isValid = valid && !error;
  const [animated, setAnimated] = useState(false);
  const isMounted = useIsMounted();
  const styles = overrideStyles(baseStyles, __overrideStyles == null ? void 0 : __overrideStyles.InputBox);
  const styleLayers = useStyleSlots(styleSlots, {
    className,
    style
  }, {
    base: {
      className: cn(styles.box, styles[`box_size_${size}`], {
        [styles[`box_variant_${variant}`]]: !!variant,
        [styles.box_error]: error,
        [styles.box_filled]: filled,
        [styles.box_focused]: focused,
        [styles.box_readOnly]: readOnly,
        [styles.box_disabled]: disabled,
        [styles.box_expandable]: expandable,
        [styles.box_noLabel]: !label,
        [styles.box_valid]: isValid,
        [styles.box_animated]: animated && !labelOutside,
        [styles.box_autofilled]: autofilled,
        [styles.box_transparent]: transparent,
        [styles.box_labelOutside]: label && labelOutside
      }, UNSAFE_className),
      style: _extends({}, corners.style(flatCorners), {
        cursor
      }, UNSAFE_style)
    },
    leftContent: styles.leftContent,
    main: styles.main,
    labelContainer: styles.labelContainer,
    label: styles.label,
    inputContainer: styles.input,
    informationContent: styles.informationContent,
    rightContainer: cn(styles.rightContainer, {
      [styles.rightContainer_expandable]: expandable
    }),
    rightContent: styles.rightContent,
    validIcon: styles.validIcon,
    liftingWrapper: styles.liftingWrapper,
    cleanButton: cn(styles.clean, {
      [styles.clean_with_fade]: mobile && !preserveSpaceForMobileCleanButton,
      [styles.clean_invisible]: mobile && preserveSpaceForMobileCleanButton,
      [styles.clean_visible]: mobile && preserveSpaceForMobileCleanButton && focused && filled
    }),
    hintBlock: styles.liftingWrapper,
    counter: styles.counter
  }, props);

  /**
   * Обходим бессмысленный ворнинг Реакта про useLayoutEffect и SSR
   * Вообще писать хуки в if - страшный грех, но конкретно в данном случае ничего страшного не произойдет,
   * т.к. на сервере он не отработает никогда, а на клиенте всегда.
   *
   * Использование useLayoutEffect в данном случае принципиально,
   * т.к. нам необходимо чтобы setAnimated отработал перед обновлением DOM
   *
   * Также выключаем правило линта, поскольку эффект должен срабатывать ТОЛЬКО при изменении focused,
   * и автофиксить зависимости ни в коем случае не нужно.
   */
  /* eslint-disable react-hooks/exhaustive-deps, react-hooks/rules-of-hooks */
  if (typeof window !== 'undefined') {
    useLayoutEffect(() => {
      if (isMounted.current && !filled) {
        setAnimated(true);
      }
    }, [focused]);
  }
  /* eslint-enable react-hooks/exhaustive-deps, react-hooks/rules-of-hooks */

  const handleAnimationEnd = () => {
    setAnimated(false);
  };
  const handleCleanerMouseDown = event => {
    event.preventDefault();
    onClean == null || onClean(event);
  };
  return /*#__PURE__*/React.createElement("div", Object.assign({}, otherProps, dataQa({
    dataQaType: cn(dataQaType, {
      [`${dataQaType}_required`]: required
    }),
    dataQaData
  }), styleLayers.base, {
    ref: forwardedRef,
    "data-focused": focused,
    "data-error": error,
    "data-input-type": dataInputType
  }), leftContent && /*#__PURE__*/React.createElement("div", Object.assign({}, styleLayers.leftContent, {
    "data-qa-type": `${dataQaType}.leftContent`
  }), leftContent), /*#__PURE__*/React.createElement("div", Object.assign({}, styleLayers.main, {
    "data-qa-type": `${dataQaType}.main`
  }), label && /*#__PURE__*/React.createElement("div", styleLayers.labelContainer, /*#__PURE__*/React.createElement("label", Object.assign({}, labelAria, styleLayers.label, {
    id: labelId,
    htmlFor: labelFor,
    onAnimationEnd: handleAnimationEnd,
    "aria-hidden": labelOutside && (focused || filled)
  }), /*#__PURE__*/React.createElement("span", {
    "data-qa-type": `${dataQaType}.label`
  }, label), required && showAsterisk && /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }, "*"))), /*#__PURE__*/React.createElement("div", Object.assign({}, styleLayers.inputContainer, {
    "data-qa-type": `${dataQaType}.inputContainer`,
    "data-cobrowsing-secure": cobrowsingSecure ? 'text' : undefined
  }), children)), informationContent && /*#__PURE__*/React.createElement("div", Object.assign({}, styleLayers.informationContent, {
    "data-qa-type": `${dataQaType}.informationContent`
  }), informationContent), (rightContent || showClean || showMobileClean || hintBlock || inputIcon || isValid || counter || showDisabledIcon) && /*#__PURE__*/React.createElement("div", Object.assign({}, styleLayers.rightContainer, {
    "data-qa-type": `${dataQaType}.rightContainer`
  }), /*#__PURE__*/React.createElement("div", Object.assign({}, styleLayers.rightContent, {
    "data-qa-type": `${dataQaType}.rightContent`
  }), showMobileClean && /*#__PURE__*/React.createElement("div", styleLayers.liftingWrapper, /*#__PURE__*/React.createElement("button", Object.assign({}, styleLayers.cleanButton, {
    onMouseDown: handleCleanerMouseDown,
    tabIndex: -1,
    type: "button",
    "aria-label": cleanButtonAriaLabel,
    "data-qa-type": `${dataQaType}.cleanButton`
  }), /*#__PURE__*/React.createElement(Icon, {
    icon: CloseIosIcon,
    iconSize: 16,
    withContainer: true,
    containerSize: 24,
    theme: {
      color: isIosInputSearch ? 'var(--tui-text-secondary)' : 'var(--tui-text-tertiary)'
    }
  }))), rightContent, isValid && /*#__PURE__*/React.createElement("div", Object.assign({}, styleLayers.validIcon, {
    "aria-hidden": "true",
    "data-qa-type": `${dataQaType}.validIcon`
  }), /*#__PURE__*/React.createElement(Icon, {
    icon: TuiIconTdsSmallPragmaticCheck,
    iconSize: 16,
    withContainer: true,
    containerSize: 24
  })), showClean && /*#__PURE__*/React.createElement("div", styleLayers.liftingWrapper, /*#__PURE__*/React.createElement("button", Object.assign({}, styleLayers.cleanButton, {
    onMouseDown: handleCleanerMouseDown,
    tabIndex: -1,
    type: "button",
    "aria-label": cleanButtonAriaLabel,
    "data-qa-type": `${dataQaType}.cleanButton`
  }), /*#__PURE__*/React.createElement(Icon, {
    icon: TuiIconTdsSmallPragmaticCross,
    iconSize: 16,
    withContainer: true,
    containerSize: size === 's' ? 16 : 24
  }))), hintBlock && /*#__PURE__*/React.createElement("div", Object.assign({
    tabIndex: -1
  }, styleLayers.hintBlock, {
    "data-qa-type": `${dataQaType}.hintBlock`,
    onMouseDown: stopEventPropagate,
    onClick: handleTooltipClick,
    onFocus: stopEventPropagate,
    onBlur: stopEventPropagate
  }), hintBlock), inputIcon, showDisabledIcon && /*#__PURE__*/React.createElement(Icon, {
    icon: TuiIconTdsSmallLock,
    iconSize: 16,
    withContainer: true,
    containerSize: 24,
    theme: {
      color: 'var(--tui-text-tertiary)'
    }
  })), typeof counter !== 'undefined' && /*#__PURE__*/React.createElement("div", Object.assign({}, styleLayers.counter, {
    "data-qa-type": `${dataQaType}.counter`
  }), counter)), scrollBar && /*#__PURE__*/React.createElement("div", {
    className: styles.scrollBar,
    "data-qa-type": `${dataQaType}.scrollBar`
  }, scrollBar));
});

// TODO: разобраться как подружить сборку TS и displayName
InputBox.displayName = 'InputBox';
export default InputBox;